<template>
  <div ref="root" class="hero" :class="{ 'is-wider': wider }">
    <div class="container has-m-v-spacing">
      <div ref="content" class="hero__block">
        <template v-if="!!$slots.title">
          <div ref="display" :class="displayCustom ? '' : 'hero__display'">
            <slot name="title" />
          </div>
        </template>
        <template v-else>
          <h1
            ref="display"
            :class="displayCustom ? '' : 'hero__display'"
            v-html="title.replace('|', '<br />')"
          />
        </template>
        <div class="hero__text content" ref="text">
          <slot />
        </div>
        <svg class="hero__svg" preserveAspectRatio="none" viewBox="0 0 731 353">
          <mask id="mask">
            <rect x="0" y="0" width="731" height="353" fill="white" />
            <path
              d="M33.301 353C14.9093 353 0 337.993 0 319.482V353H33.301Z"
              fill="black"
              ref="leftBottom"
            ></path>
            <path
              d="M0 33.5181C0 15.0066 14.9094 0 33.3011 0H0V33.5181Z"
              fill="black"
              ref="leftTop"
            ></path>
            <path
              d="M731 322.996C731 339.565 717.569 353 701 353H731V322.996Z"
              fill="black"
              ref="rightBottom"
            ></path>
            <path
              d="M697.699 124.641C716.091 124.641 731 139.648 731 158.16V0H588.579C606.97 0 621.88 15.0066 621.88 33.5181V91.1233C621.88 109.635 636.789 124.641 655.181 124.641H697.699Z"
              fill="black"
              ref="rightTop"
            ></path>
          </mask>
          <rect
            ref="block"
            x="0"
            y="0"
            width="731"
            height="353"
            fill="#E0C7E6"
            mask="url(#mask)"
          />
        </svg>
      </div>
    </div>
    <template v-if="video">
      <div ref="videoEl" class="hero__video" />
    </template>
    <template v-else-if="image">
      <responsive-image
        class="hero__img"
        :image-url="image"
        :alt="imageAlt"
        mode="all"
        :lazy-loading="false"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, useNuxtApp } from "#imports";
import type { Nullable } from "~/types/generic";
import ResponsiveImage from "~/components/global/ui/ResponsiveImage.vue";
import Player from "@vimeo/player";

const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  displayCustom: {
    type: Boolean,
    default: false,
  },
  wider: {
    type: Boolean,
    default: false,
  },
  image: {
    type: String,
    default: "/images/bg/light",
  },
  imageAlt: {
    type: String,
    default: "",
  },
  video: {
    type: Number,
  },
});

let vimeoPlayer: any = null;

const root = ref(null);
const content = ref(null);
const rightTop = ref(null);
const rightBottom = ref(null);
const leftTop = ref(null);
const leftBottom = ref(null);
const display = ref(null);
const text = ref(null);
const block = ref(null);

const videoEl = ref<Nullable<HTMLElement>>(null);

onMounted(() => {
  const { $gsap } = useNuxtApp();

  const tl = $gsap.timeline({ paused: true });

  $gsap.set(block.value, { scale: 0, opacity: 1, transformOrigin: "50% 50%" });
  $gsap.set(rightTop.value, { scale: 0.05, transformOrigin: "100% 0%" });
  $gsap.set(rightBottom.value, { scaleX: 0.05, transformOrigin: "100% 50%" });
  $gsap.set(leftTop.value, { scale: 0.05, transformOrigin: "0% 0%" });
  $gsap.set(leftBottom.value, { scale: 0.05, transformOrigin: "0% 100%" });
  $gsap.set(display.value, { y: 40, opacity: 0 });
  $gsap.set(text.value, { y: 40, opacity: 0 });
  $gsap.set(content.value, { opacity: 1 });

  tl.to(block.value, { scale: 1, opacity: 1, duration: 1, ease: "power4.out" }, 0);
  tl.to(rightTop.value, { scale: 1, duration: 2, ease: "power3.out" }, 0.1);
  tl.to(rightBottom.value, { scaleX: 1, duration: 2.5, ease: "power3.out" }, 0.1);
  tl.to(leftTop.value, { scale: 1, duration: 4, ease: "power4.out" }, 0.2);
  tl.to(leftBottom.value, { scale: 1, duration: 4, ease: "power4.out" }, 0.2);
  tl.to(display.value, { y: 0, opacity: 1, duration: 1, ease: "power3.out" }, 0.5);
  tl.to(text.value, { y: 0, opacity: 1, duration: 1, ease: "power3.out" }, 0.8);

  setTimeout(() => {
    tl.play();
  }, 350);

  if (videoEl.value) {
    vimeoPlayer = new Player(videoEl.value, {
      id: props.video,
      background: true,
      loop: true,
      autoplay: true,
    });
  }
});

onBeforeUnmount(() => vimeoPlayer?.unload());
</script>

<style lang="scss" scoped>
.hero {
  position: relative;
  overflow: hidden;

  &__video {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    pointer-events: none;
    overflow: hidden;
    position: absolute;

    :deep(iframe) {
      width: 100vw;
      height: 56.25vw;
      /* Given a 16:9 aspect ratio, 9/16*100 = 56.25 */
      min-height: 100vh;
      min-width: 177.77vh;
      /* Given a 16:9 aspect ratio, 16/9*100 = 177.77 */
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    //@include cover(0, -1);
  }

  &__img {
    @include cover(0, -1);

    ::v-deep(img) {
      @include cover;
    }
  }

  &__display {
    padding: rem(40) rem(40) 0;
    font-family: var(--heading-font-family);
    @include fluid(font-size, 32, 60, 70);
    line-height: 1.02;
  }

  &.is-wider {
    .hero__block {
      @include fluid(width, 320, 640, 690);
    }
  }

  &__block {
    opacity: 0;
    @include fluid(width, 320, 550, 630);

    position: relative;
    z-index: 2;

    @include mobile {
      width: 100%;
      background-color: var(--dark-pink);
      border-radius: rem(20);
    }

    @include desktop {
      margin-left: rem(-40);
    }
  }

  &__text {
    padding: rem(40);
    font-weight: 500;
    @include fluid(font-size, 15, 17, 18);
  }

  &__svg {
    @include cover($z-index: -1);

    @include mobile {
      display: none;
    }

    * {
      transform-box: view-box;
    }
  }
}
</style>
